import React from "react";

import { Alert, Spin, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  LineChart,
  Line,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
  TooltipProps,
} from "recharts";

import { hoursReadable, formatTime, getLimitActivity } from "../../utils";
import { useProjectTimeStats } from "../../hooks/useProjectTimeStats";

const ActivityColor: Record<string, string> = {
  Development: "#8884d8",
  Management: "#82ca9d",
  PTO: "#6495ED",
  Accounting: "#FFD700",
  QA: "#808000",
  Holiday: "#FFEBCD",
  "C-Level": "#8A2BE2",
  total: "#DC143C",
};

const CustomTooltip = ({
  active,
  payload,
  coordinate,
  viewBox,
}: TooltipProps<number, string>) => {
  if (!active || !payload || !payload.length) return null;

  const x = coordinate?.x ?? 0;
  const chartWidth = viewBox?.width ?? 0;

  const isNearRightEdge = x > chartWidth - 200;

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 min-w-[180px]"
      style={{
        transform: isNearRightEdge ? "translateX(-100%)" : "translateX(0)",
      }}
    >
      <p className="text-gray-900 font-semibold text-sm mb-2">
        {payload[0].payload.name}
      </p>

      <div className="flex flex-col gap-1">
        {payload.map((chartLine, index) => (
          <div
            key={index}
            className="flex items-center justify-between text-sm"
          >
            <span
              className="flex items-center gap-2"
              style={{ color: chartLine.color }}
            >
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: chartLine.color }}
              />
              {chartLine.dataKey}
            </span>

            <span className="font-medium text-gray-800">
              {hoursReadable(Number(chartLine.value))}
            </span>
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-400 mt-2">
        Click on the chart to view summary
      </p>
    </div>
  );
};

interface ProjectTimeStatsProps {
  project_id: number;
  fromDate: Date;
  toDate: Date;
}

interface LimitActivity {
  activity: string;
  time: number[];
}

const summaryColumns: ColumnsType<{ user_login: string; hours: number }> = [
  {
    title: "User",
    dataIndex: "user_login",
    key: "user_login",
    width: "60%",
  },
  {
    title: "Hours",
    dataIndex: "hours",
    key: "hours",
    width: "40%",
    align: "center",
    render: (_, { hours }) => hoursReadable(hours),
  },
];

const ProjectTimeStats: React.FC<ProjectTimeStatsProps> = ({
  project_id,
  fromDate,
  toDate,
}) => {
  const {
    stats,
    chartData,
    activities,
    focusInfo,
    summaryTitle,
    averageTotalHours,
    summaryRef,
    loadingStats,
    fetchStatsError,
    onTooltipClick,
  } = useProjectTimeStats(project_id, fromDate, toDate);

  if (loadingStats && !stats) {
    return (
      <div className="w-full h-full flex items-center justify-center py-16 bg-gray-50">
        <Spin size="large" />
      </div>
    );
  }

  if (fetchStatsError) {
    return (
      <div className="px-12 py-8 bg-gray-50">
        <Alert
          type="error"
          showIcon
          message="Project time stats could not be loaded."
          description={fetchStatsError}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-6">
          Monthly Activity Overview
        </h3>
        <div className="w-full h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              onClick={(state) => {
                if (state?.activePayload?.length) {
                  const monthData = state.activePayload[0].payload;
                  onTooltipClick(monthData);
                }
              }}
            >
              <CartesianGrid
                stroke="#e5e7eb"
                strokeDasharray="4 4"
                vertical={false}
              />
              <XAxis
                dataKey="name"
                tickFormatter={(value) =>
                  typeof value === "string" ? value.slice(0, 3) : value
                }
                tick={{ fill: "#6B7280", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tickFormatter={(v) => hoursReadable(v)}
                tick={{ fill: "#6B7280", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={<CustomTooltip />}
                allowEscapeViewBox={{ x: true, y: true }}
              />
              <Legend
                verticalAlign="top"
                height={36}
                iconType="circle"
                wrapperStyle={{ fontSize: "12px" }}
              />

              {/* Reference lines */}
              {stats?.bitmaker_settings.references?.map(
                (limitActivity: LimitActivity) =>
                  limitActivity.time.map((time: number) => (
                    <ReferenceLine
                      key={`${limitActivity.activity}-${time}`}
                      y={time}
                      stroke={ActivityColor[limitActivity.activity]}
                      strokeOpacity={0.4}
                    />
                  )),
              )}

              {stats?.bitmaker_settings.limits?.map((limit) =>
                Object.keys(limit).map((key: string) => {
                  const activity = getLimitActivity(
                    stats?.bitmaker_settings.limits,
                    limit,
                    key,
                  );
                  const limitTime = limit[key]?.time;
                  if (!limitTime) return null;

                  return (
                    <ReferenceLine
                      key={`limit-${activity}-${key}`}
                      y={formatTime(limitTime)}
                      stroke={ActivityColor[activity] ?? "#999"}
                      strokeDasharray="4 4"
                      strokeOpacity={0.6}
                      label={{
                        position: "top",
                        value: `Limit: ${activity} ${formatTime(limitTime)}h`,
                        fill: ActivityColor[activity] ?? "#999",
                        fontSize: 14,
                      }}
                    />
                  );
                }),
              )}

              {/* Reference line for total average hours */}
              {averageTotalHours > 0 && (
                <ReferenceLine
                  y={averageTotalHours}
                  stroke="#1E3A8A"
                  strokeDasharray="4 4"
                  strokeOpacity={0.6}
                  label={{
                    position: "top",
                    value: `Average: ${hoursReadable(averageTotalHours)}`,
                    fill: "#00008B",
                    fontSize: 14,
                  }}
                />
              )}

              {/* Lines */}
              {activities.map((activity) => (
                <Line
                  key={`line-${activity}`}
                  type="monotone"
                  dataKey={activity}
                  stroke={ActivityColor[activity] ?? "#999"}
                  strokeWidth={1.8}
                  dot={false}
                  activeDot={{ r: 6, cursor: "pointer" }}
                  animationDuration={400}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Summary Table */}
      {focusInfo && focusInfo?.length > 0 && (
        <div
          className="bg-white border rounded-2xl shadow-sm p-6"
          ref={summaryRef}
        >
          {summaryTitle && (
            <h3 className="text-sm font-semibold text-gray-700 mb-6 text-center">
              Summary for {summaryTitle}
            </h3>
          )}

          <Table
            columns={summaryColumns}
            dataSource={focusInfo.map((user) => ({
              key: user.user_login,
              user_login: user.user_login,
              hours: user.hours || 0,
            }))}
            pagination={false}
            rowClassName={() =>
              "hover:bg-gray-50/80 transition-colors cursor-pointer"
            }
            size="middle"
            bordered={false}
          />
        </div>
      )}
    </div>
  );
};

export default ProjectTimeStats;
